import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import { API_URL, USERDATA } from "../../../constants";
import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";
import { trackPurchase } from "../../../components/MetaPixel/MetaPixel";

const POLL_INTERVAL_MS = 3000;
const TIMEOUT_MS = 60000;

export default function StripeSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { authLoading } = useAuth();
  const { t } = useTranslation();

  // "polling" | "success" | "timeout" | "missing_session"
  const [phase, setPhase] = useState("polling");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (authLoading) return;

    if (!sessionId) {
      setPhase("missing_session");
      return;
    }

    if (phase !== "polling") return;

    let isMounted = true;
    let intervalId;
    const startTime = Date.now();

    const poll = async () => {
      try {
        const [profileRes, labVisitsRes, userRes] = await Promise.all([
          axios.get(`${API_URL}/api/profile/stripecustomer/`),
          axios.get(`${API_URL}/api/user-lab-visits/`),
          axios.get(USERDATA),
        ]);

        if (!isMounted) return;

        const paymentSucceeded = profileRes.data?.last_payment_status === "succeeded";
        const labVisits = Array.isArray(labVisitsRes.data) ? labVisitsRes.data : [];
        const labVisitCreated = labVisits.some(
          (visit) => visit.subscription_period === 1 && visit.visit_number === 1
        );
        const onboardingAdvanced = (userRes.data?.onboarding_status ?? 0) >= 2;

        if (paymentSucceeded && labVisitCreated && onboardingAdvanced) {
          clearInterval(intervalId);
          setPhase("success");
          return;
        }
      } catch (err) {
        console.error("StripeSuccess polling failed:", err);
      }

      if (isMounted) {
        const elapsed = Date.now() - startTime;
        setElapsedMs(elapsed);
        if (elapsed >= TIMEOUT_MS) {
          clearInterval(intervalId);
          setPhase("timeout");
        }
      }
    };

    poll();
    intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [authLoading, sessionId, phase, retryToken, navigate]);

  // Separate from the polling effect above: that effect re-runs (and tears down its
  // own isMounted flag) the instant setPhase("success") fires, since phase is one of
  // its dependencies — so a setTimeout scheduled inside it would always see a stale
  // isMounted=false by the time it fires. This effect's lifecycle is independent.
  useEffect(() => {
    if (phase !== "success") return;
    trackPurchase();
    const timeoutId = setTimeout(() => navigate("/FirstLabvisit", { replace: true }), 1500);
    return () => clearTimeout(timeoutId);
  }, [phase, navigate]);

  const handleRetry = () => {
    setElapsedMs(0);
    setPhase("polling");
    setRetryToken((token) => token + 1);
  };

  const handleContactSupport = () => {
    window.location.href = "mailto:office@speak23.com";
  };

  const waitingMessage =
    elapsedMs < 8000
      ? t("onb_ss_confirming")
      : elapsedMs < 20000
      ? t("onb_ss_setting_up")
      : t("onb_ss_long_wait");

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          borderRadius: 3,
          boxShadow: 3,
          backgroundColor: "background.paper",
        }}
      >
        {phase === "missing_session" && (
          <>
            <Typography variant="h5" color="error" gutterBottom>
              {t("onb_ss_missing_session_title")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t("onb_ss_missing_session_body")}
            </Typography>
          </>
        )}

        {phase === "timeout" && (
          <>
            <Typography variant="h5" gutterBottom>
              {t("onb_ss_timeout_title")}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              {t("onb_ss_timeout_body")}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button variant="contained" color="primary" onClick={handleRetry}>
                {t("onb_ss_retry")}
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleContactSupport}>
                {t("onb_ss_contact_support")}
              </Button>
            </Stack>
          </>
        )}

        {phase === "success" && (
          <>
            <Typography variant="h5" gutterBottom>
              {t("onb_ss_success_title")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t("onb_ss_success_body")}
            </Typography>
          </>
        )}

        {phase === "polling" && (
          <>
            <Typography variant="h5" gutterBottom>
              {t("onb_ss_title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {waitingMessage}
            </Typography>
            <CircularProgress color="primary" />
          </>
        )}
      </Box>
    </Container>
  );
}
